import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReferencesSection from '../../components/ReferencesSection';

import { API_BASE as API } from '../../config/api';

const TYPOLOGIES = [
  { value: 'analytic', label: 'Analytic (Isolating)', desc: 'Words rarely change form; grammar expressed by word order and particles (e.g., Chinese, Vietnamese)' },
  { value: 'fusional', label: 'Fusional', desc: 'Single morphemes express multiple grammatical categories (e.g., Spanish, Russian)' },
  { value: 'agglutinative', label: 'Agglutinative', desc: 'Morphemes are clearly separable, each expressing one category (e.g., Turkish, Finnish)' },
  { value: 'polysynthetic', label: 'Polysynthetic', desc: 'Words can contain many morphemes, sometimes forming whole sentences (e.g., Inuit, Mohawk)' },
];

const MorphologyDescPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [typology, setTypology] = useState('analytic');
  const [description, setDescription] = useState('');
  const [morphemeBoundaries, setMorphemeBoundaries] = useState('');
  const [fusionDegree, setFusionDegree] = useState('');

  useEffect(() => { fetchData(); }, [conlangId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/morphology-description`, { withCredentials: true });
      if (res.data) {
        setTypology(res.data.typology || 'analytic');
        setDescription(res.data.description || '');
        setMorphemeBoundaries(res.data.morpheme_boundaries || '');
        setFusionDegree(res.data.fusion_degree || '');
      }
    } catch { /* no data yet */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/conlangs/${conlangId}/morphology-description`, {
        typology,
        description: description || null,
        morpheme_boundaries: morphemeBoundaries || null,
        fusion_degree: fusionDegree || null,
      }, { withCredentials: true });
      toast.success('Morphology description saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="morph-desc-title">Morphology Overview</h2>
          <p className="text-foreground-muted mt-1">Describe the morphological typology of {conlang?.name}</p>
        </div>
        {canEdit && (
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-morph-desc-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
        )}
      </div>

      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Morphological Typology</CardTitle>
          <CardDescription>What kind of morphological system does your language use?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={typology} onValueChange={setTypology}>
            <SelectTrigger className="bg-background border-border" data-testid="typology-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background-paper border-border">
              {TYPOLOGIES.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  <div><span className="font-medium">{t.label}</span></div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-foreground-subtle">
            {TYPOLOGIES.find(t => t.value === typology)?.desc}
          </p>
        </CardContent>
      </Card>

      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">General Description</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the overall morphological system. How productive is word formation? What processes are most common? How complex are typical words?"
            className="bg-background border-border min-h-[150px]"
            data-testid="morph-description"
          />
        </CardContent>
      </Card>

      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Morpheme Boundaries</CardTitle>
          <CardDescription>How do morphemes combine?</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={morphemeBoundaries}
            onChange={(e) => setMorphemeBoundaries(e.target.value)}
            placeholder="Describe morpheme boundaries, morphophonological alternations, allomorphy patterns, and how morphemes combine..."
            className="bg-background border-border min-h-[120px]"
            data-testid="morpheme-boundaries"
          />
        </CardContent>
      </Card>

      {(typology === 'fusional' || typology === 'polysynthetic') && (
        <Card className="bg-background-paper border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Fusion Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={fusionDegree}
              onChange={(e) => setFusionDegree(e.target.value)}
              placeholder="Describe the degree of fusion: how many categories are expressed per morpheme? Are there portmanteau morphs?"
              className="bg-background border-border min-h-[100px]"
              data-testid="fusion-degree"
            />
          </CardContent>
        </Card>
      )}

      <div className="p-4 rounded-md bg-primary/10 border border-primary/30 flex gap-3">
        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-primary text-sm">
          <strong>Morphological Typology</strong> classifies how a language builds words. Most real languages are a mix of types, so feel free to describe your language's unique characteristics.
        </div>
      </div>
      <ReferencesSection section="morphology" />
    </div>
  );
};

export default MorphologyDescPage;
